// Перевод организации в подразделение — «Сделать подразделением…» (037: FR-017 – FR-025; research,
// Р6, Р7; data-model.md; ORG-11 – ORG-15). Организация становится подразделением другой организации:
// её подразделения, места работы и кураторства переезжают, сама она уходит в архив с пометкой.
// «Вернуть организацию» возвращает то, что не меняли после перевода. Ссылки протоколов и доступов
// перевод не переносит — с ними перевести нельзя (DIR-019).
import { z } from '../kernel/zod.ts';
import { IdSchema, PersonRefSchema, TimestampSchema, VersionSchema } from '../kernel/common.ts';
import { RecordRefSchema, UnitRefSchema } from './record.ts';
import { UnitNameInputSchema } from './unit.ts';

const count = z.number().int().min(0);

/** Сколько подразделений, мест работы и кураторств переезжает или вернётся. */
export const UnitConversionCountsSchema = z.object({
  units: count,
  workplaces: count,
  curatorships: count,
});
export type UnitConversionCounts = z.infer<typeof UnitConversionCountsSchema>;

/** Окно «Сделать подразделением…»: куда и под каким названием. */
export const UnitConversionPreviewQuerySchema = z.object({
  targetId: IdSchema,
  /** Нет — корень цели. */
  parentUnitId: IdSchema.optional(),
  /** Нет — название организации. */
  name: z.string().trim().max(200).optional(),
});
export type UnitConversionPreviewQuery = z.output<typeof UnitConversionPreviewQuerySchema>;

/** Ссылки, при которых перевод запрещён (FR-022); все нули — можно. */
export const UnitConversionBlockersSchema = z.object({
  /** Пункты протоколов, кроме удалённых навсегда: организация — ответственный или контрагент. */
  items: count,
  /** Упоминания и «создано из ячейки» в протоколах, кроме удалённых навсегда. */
  mentions: count,
  /** Зоны ответственности документов. */
  zones: count,
  /** Выдачи протоколов, где организация в зоне редактора. */
  grants: count,
  // 041 (FR-022, ревью T049): областей у ролей нет — блокера «Роли пользователей» нет.
});
export type UnitConversionBlockers = z.infer<typeof UnitConversionBlockersSchema>;

export const UnitConversionPreviewSchema = z.object({
  counts: UnitConversionCountsSchema.extend({
    /** Места работы, которые уточнятся: у сотрудника уже есть место в цели (FR-020). */
    merged: count,
  }),
  blockers: UnitConversionBlockersSchema,
  /** Название занято на выбранном уровне цели (DIR-012). */
  nameTaken: z.boolean(),
  /** Будущий путь: цель › … › название. */
  path: z.array(z.string()),
});
export type UnitConversionPreview = z.infer<typeof UnitConversionPreviewSchema>;

export const CreateUnitConversionBodySchema = z.object({
  organizationId: IdSchema,
  /** Версия организации из карточки: изменили после — REC-001. */
  version: VersionSchema,
  targetId: IdSchema,
  /** null — корень цели. */
  parentUnitId: IdSchema.nullable(),
  name: UnitNameInputSchema,
});
export type CreateUnitConversionBody = z.output<typeof CreateUnitConversionBodySchema>;

export const UnitConversionParamsSchema = z.object({ id: IdSchema });

export const UnitConversionSchema = z.object({
  id: IdSchema,
  /** Переведённая организация. */
  organization: RecordRefSchema,
  /** Новое подразделение. */
  unit: UnitRefSchema,
  target: RecordRefSchema,
  /** От корня цели до нового подразделения включительно. */
  path: z.array(UnitRefSchema),
  moved: UnitConversionCountsSchema,
  at: TimestampSchema,
  by: PersonRefSchema,
  undoneAt: TimestampSchema.nullable(),
});
export type UnitConversion = z.infer<typeof UnitConversionSchema>;

/** Действующий перевод в карточке переведённой организации и числа «Вернуть организацию». */
export const OrganizationUnitConversionSchema = z.object({
  conversion: UnitConversionSchema,
  undo: z.object({
    returning: UnitConversionCountsSchema,
    staying: UnitConversionCountsSchema,
    /** Новое подразделение уйдёт в архив: в нём ничего не останется. */
    unitArchived: z.boolean(),
  }),
});
export type OrganizationUnitConversion = z.infer<typeof OrganizationUnitConversionSchema>;
